<script setup>
import { ref, computed, inject } from 'vue';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Spinner from 'shared/components/Spinner.vue';

const router = useRouter();
const currentAccountId = useMapGetter('getCurrentAccountId');

// Get data from parent (already fetched centrally with 5s refresh)
const {
  liveMonitorData,
  isLoading,
  lastUpdated: parentLastUpdated,
} = inject('analyticsData');

// Local state
const activeTab = ref('all'); // 'all', 'humans', 'bots', 'ai'
const selectedConversation = ref(null);

// Use monitor data from parent
const monitorData = computed(() => liveMonitorData.value);
const lastUpdated = computed(() => parentLastUpdated.value);

// Computed
const summary = computed(() => monitorData.value?.summary || {});
const humanAgents = computed(() => monitorData.value?.human_agents || []);
const bots = computed(() => monitorData.value?.bots || []);
const aiAgents = computed(() => monitorData.value?.ai_agents || []);
const recentMessages = computed(() => monitorData.value?.recent_messages || []);

// All active handlers (combined)
const allHandlers = computed(() => {
  const handlers = [];

  humanAgents.value.forEach(agent => {
    if (agent.conversations_count > 0) {
      handlers.push({ ...agent, handlerType: 'human' });
    }
  });

  bots.value.forEach(bot => {
    if (bot.conversations_count > 0) {
      handlers.push({ ...bot, handlerType: 'bot' });
    }
  });

  aiAgents.value.forEach(ai => {
    if (ai.conversations_count > 0) {
      handlers.push({ ...ai, handlerType: 'ai' });
    }
  });

  return handlers.sort((a, b) => b.conversations_count - a.conversations_count);
});

// Filtered handlers based on active tab
const filteredHandlers = computed(() => {
  if (activeTab.value === 'all') return allHandlers.value;
  if (activeTab.value === 'humans')
    return humanAgents.value.filter(a => a.conversations_count > 0);
  if (activeTab.value === 'bots')
    return bots.value.filter(b => b.conversations_count > 0);
  if (activeTab.value === 'ai')
    return aiAgents.value.filter(a => a.conversations_count > 0);
  return allHandlers.value;
});

// Navigate to conversation
function goToConversation(conversationId) {
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: currentAccountId.value,
      conversation_id: conversationId,
    },
  });
}

// Open conversation detail
function openConversationDetail(conversation, handler) {
  selectedConversation.value = { ...conversation, handler };
}

function closeConversationDetail() {
  selectedConversation.value = null;
}

// Format helpers
function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'agora';
  if (diffMins < 60) return `${diffMins}min`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h`;
  return `${Math.floor(diffMins / 1440)}d`;
}

function formatFullTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatLastUpdated() {
  return lastUpdated.value.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function getHandlerIcon(type) {
  if (type === 'human') return 'i-lucide-headphones';
  if (type === 'bot') return 'i-lucide-bot';
  if (type === 'ai') return 'i-lucide-sparkles';
  return 'i-lucide-user';
}

function getHandlerColor(type) {
  if (type === 'human') return 'bg-n-blue-9';
  if (type === 'bot') return 'bg-n-violet-9';
  if (type === 'ai') return 'bg-n-teal-9';
  return 'bg-n-slate-9';
}

function getInboxIcon(channelType) {
  const iconMap = {
    'Channel::WebWidget': 'i-lucide-globe',
    'Channel::Api': 'i-lucide-webhook',
    'Channel::Whatsapp': 'i-lucide-message-circle',
    'Channel::FacebookPage': 'i-lucide-facebook',
    'Channel::Email': 'i-lucide-mail',
    'Channel::Telegram': 'i-lucide-send',
  };
  return iconMap[channelType] || 'i-lucide-inbox';
}

function getMessageBubbleClass(message) {
  if (message.sender_type === 'Contact') {
    return 'bg-n-alpha-2 mr-auto';
  }
  if (message.private) {
    return 'bg-n-amber-3 ml-auto';
  }
  return 'bg-n-blue-9 text-white ml-auto';
}

function getSenderIcon(senderType) {
  if (senderType === 'Contact') return 'i-lucide-user';
  if (senderType === 'AgentBot') return 'i-lucide-bot';
  return 'i-lucide-headphones';
}

// Data is now refreshed every 5 seconds by the parent component
</script>

<template>
  <div class="flex flex-col h-full gap-4">
    <!-- Header with Summary Stats -->
    <div
      class="flex flex-wrap items-center justify-between gap-4 px-4 py-3 rounded-xl border border-n-weak bg-n-solid-2"
    >
      <div class="flex items-center gap-6">
        <div>
          <h2 class="text-base font-semibold text-n-slate-12">
            Monitor em Tempo Real
          </h2>
          <p class="text-sm text-n-slate-11">
            Acompanhe todas as conversas ao vivo
          </p>
        </div>

        <!-- Summary Stats -->
        <div class="flex items-center gap-3">
          <div
            class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-blue-3"
          >
            <span class="i-lucide-message-square text-n-blue-11" />
            <span class="text-sm font-medium text-n-blue-11">{{ summary.total_active || 0 }} ativas</span>
          </div>
          <div
            class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-teal-3"
          >
            <span class="i-lucide-headphones text-n-teal-11" />
            <span class="text-sm font-medium text-n-teal-11">{{ summary.human_handling || 0 }} humanos</span>
          </div>
          <div
            class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-violet-3"
          >
            <span class="i-lucide-bot text-n-violet-11" />
            <span class="text-sm font-medium text-n-violet-11">{{ summary.bot_handling || 0 }} bots</span>
          </div>
          <div
            class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-n-amber-3"
          >
            <span class="i-lucide-sparkles text-n-amber-11" />
            <span class="text-sm font-medium text-n-amber-11">{{ summary.ai_handling || 0 }} IA</span>
          </div>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <div class="flex items-center gap-2">
          <span class="w-2 h-2 rounded-full bg-n-teal-9 animate-pulse" />
          <span class="text-xs text-n-slate-10">{{ formatLastUpdated() }}</span>
        </div>
        <button
          class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border border-n-weak bg-n-alpha-1 hover:bg-n-alpha-2 transition-colors"
          :disabled="isLoading"
          @click="fetchMonitorData"
        >
          <span
            class="i-lucide-refresh-cw"
            :class="{ 'animate-spin': isLoading }"
          />
          Atualizar
        </button>
      </div>
    </div>

    <!-- Tab Filters -->
    <div class="flex items-center gap-2 px-2">
      <button
        v-for="tab in [
          { id: 'all', label: 'Todos', icon: 'i-lucide-users' },
          {
            id: 'humans',
            label: 'Agentes Humanos',
            icon: 'i-lucide-headphones',
          },
          { id: 'bots', label: 'Bots', icon: 'i-lucide-bot' },
          { id: 'ai', label: 'Agentes IA', icon: 'i-lucide-sparkles' },
        ]"
        :key="tab.id"
        class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors"
        :class="
          activeTab === tab.id
            ? 'bg-n-blue-9 text-white'
            : 'bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2'
        "
        @click="activeTab = tab.id"
      >
        <span :class="tab.icon" />
        {{ tab.label }}
      </button>
    </div>

    <!-- Loading State -->
    <div
      v-if="isLoading && !monitorData"
      class="flex items-center justify-center py-20"
    >
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="filteredHandlers.length === 0"
      class="flex flex-col items-center justify-center py-20 text-n-slate-11"
    >
      <span class="i-lucide-inbox text-4xl mb-3" />
      <p class="text-base font-medium">Nenhuma conversa ativa</p>
      <p class="text-sm">As conversas em andamento aparecerao aqui</p>
    </div>

    <!-- Main Content - Split View -->
    <div v-else class="flex-1 flex gap-4 min-h-0">
      <!-- Left: Handlers List -->
      <div class="w-80 flex flex-col gap-3 overflow-y-auto pr-2">
        <div
          v-for="handler in filteredHandlers"
          :key="`${handler.type}-${handler.id}`"
          class="rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden"
        >
          <!-- Handler Header -->
          <div class="flex items-center gap-3 p-3 border-b border-n-weak">
            <div class="relative">
              <Avatar
                v-if="handler.thumbnail"
                :src="handler.thumbnail"
                :name="handler.name"
                :size="36"
                rounded-full
              />
              <div
                v-else
                class="w-9 h-9 rounded-full flex items-center justify-center text-white"
                :class="getHandlerColor(handler.handlerType || handler.type)"
              >
                <span
                  :class="getHandlerIcon(handler.handlerType || handler.type)"
                />
              </div>
              <span
                v-if="handler.availability === 'online'"
                class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-n-teal-9 border-2 border-n-solid-2"
              />
            </div>
            <div class="flex-1 min-w-0">
              <h4 class="text-sm font-semibold text-n-slate-12 truncate">
                {{ handler.name }}
              </h4>
              <div class="flex items-center gap-2 text-xs text-n-slate-11">
                <span
                  class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                  :class="{
                    'bg-n-blue-3 text-n-blue-11':
                      handler.handlerType === 'human' ||
                      handler.type === 'human',
                    'bg-n-violet-3 text-n-violet-11':
                      handler.handlerType === 'bot' || handler.type === 'bot',
                    'bg-n-teal-3 text-n-teal-11':
                      handler.handlerType === 'ai' || handler.type === 'ai',
                  }"
                >
                  {{
                    handler.handlerType === 'human' || handler.type === 'human'
                      ? 'Agente'
                      : handler.handlerType === 'bot' || handler.type === 'bot'
                        ? 'Bot'
                        : 'IA'
                  }}
                </span>
                <span>{{ handler.conversations_count }} conversas</span>
              </div>
            </div>
          </div>

          <!-- Handler's Conversations -->
          <div class="max-h-64 overflow-y-auto">
            <div
              v-for="conv in handler.conversations"
              :key="conv.id"
              class="flex items-center gap-2 p-2 hover:bg-n-alpha-1 cursor-pointer border-b border-n-weak last:border-b-0"
              @click="openConversationDetail(conv, handler)"
            >
              <Avatar
                :src="conv.contact?.thumbnail"
                :name="conv.contact?.name || 'Contato'"
                :size="32"
                rounded-full
              />
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-1">
                  <span class="text-xs font-medium text-n-slate-12 truncate">{{
                    conv.contact?.name || 'Contato'
                  }}</span>
                  <span class="text-[10px] text-n-slate-10">#{{ conv.display_id }}</span>
                </div>
                <div class="flex items-center gap-1">
                  <span
                    :class="getInboxIcon(conv.inbox?.channel_type)"
                    class="text-[10px] text-n-slate-10"
                  />
                  <span class="text-[10px] text-n-slate-10 truncate">{{
                    conv.inbox?.name
                  }}</span>
                </div>
              </div>
              <div class="flex flex-col items-end gap-0.5">
                <span class="text-[10px] text-n-slate-10">{{
                  formatTime(conv.last_activity_at)
                }}</span>
                <span
                  v-if="conv.unread_count > 0"
                  class="w-4 h-4 flex items-center justify-center text-[9px] font-bold rounded-full bg-n-ruby-9 text-white"
                >
                  {{ conv.unread_count > 9 ? '9+' : conv.unread_count }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Conversation Detail or Recent Messages Feed -->
      <div
        class="flex-1 rounded-xl border border-n-weak bg-n-solid-2 overflow-hidden flex flex-col"
      >
        <!-- Conversation Detail View -->
        <template v-if="selectedConversation">
          <!-- Header -->
          <div
            class="flex items-center justify-between p-4 border-b border-n-weak"
          >
            <div class="flex items-center gap-3">
              <button
                class="p-1.5 rounded-lg hover:bg-n-alpha-2"
                @click="closeConversationDetail"
              >
                <span class="i-lucide-arrow-left" />
              </button>
              <Avatar
                :src="selectedConversation.contact?.thumbnail"
                :name="selectedConversation.contact?.name || 'Contato'"
                :size="40"
                rounded-full
              />
              <div>
                <h3 class="text-sm font-semibold text-n-slate-12">
                  {{ selectedConversation.contact?.name || 'Contato' }}
                </h3>
                <div class="flex items-center gap-2 text-xs text-n-slate-11">
                  <span>#{{ selectedConversation.display_id }}</span>
                  <span>|</span>
                  <span
                    :class="
                      getInboxIcon(selectedConversation.inbox?.channel_type)
                    "
                  />
                  <span>{{ selectedConversation.inbox?.name }}</span>
                </div>
              </div>
            </div>
            <button
              class="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg bg-n-blue-9 text-white hover:bg-n-blue-10"
              @click="goToConversation(selectedConversation.id)"
            >
              <span class="i-lucide-external-link" />
              Abrir Conversa
            </button>
          </div>

          <!-- Messages -->
          <div class="flex-1 overflow-y-auto p-4 space-y-3">
            <div
              v-for="msg in selectedConversation.messages"
              :key="msg.id"
              class="flex flex-col max-w-[80%]"
              :class="
                msg.sender_type === 'Contact'
                  ? 'items-start'
                  : 'items-end ml-auto'
              "
            >
              <div class="flex items-center gap-1 mb-1">
                <span
                  :class="getSenderIcon(msg.sender_type)"
                  class="text-xs text-n-slate-10"
                />
                <span class="text-xs text-n-slate-10">{{
                  msg.sender_name
                }}</span>
                <span class="text-[10px] text-n-slate-9">{{
                  formatFullTime(msg.created_at)
                }}</span>
              </div>
              <div
                class="px-3 py-2 rounded-xl text-sm"
                :class="getMessageBubbleClass(msg)"
              >
                <p class="whitespace-pre-wrap break-words">
                  {{ msg.content || '[Sem conteudo]' }}
                </p>
              </div>
            </div>
          </div>

          <!-- Handler Info -->
          <div class="p-3 border-t border-n-weak bg-n-alpha-1">
            <div class="flex items-center gap-2 text-xs text-n-slate-11">
              <span
                :class="getHandlerIcon(selectedConversation.handler?.type)"
              />
              <span>Atendido por:</span>
              <span class="font-medium text-n-slate-12">{{
                selectedConversation.handler?.name
              }}</span>
            </div>
          </div>
        </template>

        <!-- Recent Messages Feed -->
        <template v-else>
          <div
            class="flex items-center justify-between p-4 border-b border-n-weak"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-activity text-n-blue-11" />
              <h3 class="text-sm font-semibold text-n-slate-12">
                Feed de Mensagens
              </h3>
            </div>
            <span class="text-xs text-n-slate-10">Ultimas mensagens de todas as conversas</span>
          </div>

          <div class="flex-1 overflow-y-auto">
            <div
              v-for="msg in recentMessages"
              :key="msg.id"
              class="flex items-start gap-3 p-3 border-b border-n-weak hover:bg-n-alpha-1 cursor-pointer"
              @click="goToConversation(msg.conversation_id)"
            >
              <div
                class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                :class="
                  msg.sender_type === 'Contact' ? 'bg-n-slate-5' : 'bg-n-blue-9'
                "
              >
                <span
                  class="text-sm"
                  :class="[
                    getSenderIcon(msg.sender_type),
                    msg.sender_type === 'Contact'
                      ? 'text-n-slate-11'
                      : 'text-white',
                  ]"
                />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-0.5">
                  <span class="text-xs font-medium text-n-slate-12">{{
                    msg.sender_name
                  }}</span>
                  <span class="text-[10px] text-n-slate-9">{{
                    msg.sender_type === 'Contact' ? 'enviou' : 'respondeu'
                  }}</span>
                  <span class="text-[10px] text-n-slate-10">#{{ msg.conversation_display_id }}</span>
                </div>
                <p class="text-sm text-n-slate-11 truncate">
                  {{ msg.content || '[Anexo]' }}
                </p>
                <div
                  class="flex items-center gap-2 mt-1 text-[10px] text-n-slate-10"
                >
                  <span>{{ msg.contact_name }}</span>
                  <span>|</span>
                  <span>{{ msg.inbox_name }}</span>
                  <span>|</span>
                  <span>{{ formatFullTime(msg.created_at) }}</span>
                </div>
              </div>
              <span
                v-if="msg.message_type === 'incoming'"
                class="px-1.5 py-0.5 text-[10px] rounded bg-n-teal-3 text-n-teal-11"
              >
                Entrada
              </span>
              <span
                v-else
                class="px-1.5 py-0.5 text-[10px] rounded bg-n-blue-3 text-n-blue-11"
              >
                Saida
              </span>
            </div>

            <div
              v-if="recentMessages.length === 0"
              class="flex flex-col items-center justify-center py-20 text-n-slate-11"
            >
              <span class="i-lucide-message-square text-3xl mb-2" />
              <p class="text-sm">Nenhuma mensagem recente</p>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
